import { useRouter } from '@components/Router/useRouter'
import { getOrganizationId } from '@lib/graphql/variables/organizationId'
import useUser from '@lib/hooks/useUser'
import { handleOrganizationRoutes } from '@mono/packages/utils/router'
import {
  ADMIN_MODULE_PERMISSIONS,
  ORDER_PERMISSION_PREFIX,
  PERMISSIONS,
  REPORT_ROUTE_PERMISSIONS
} from '@utils/constants/permissions'
import { ROUTER_PATHS } from '@utils/constants/routerPaths'
import { useEffect, useState } from 'react'

const rootPaths = [
  ROUTER_PATHS.ORGANIZATION_PICKER,
  ROUTER_PATHS.NOT_FOUND,
  ROUTER_PATHS.FORBIDDEN,
  ROUTER_PATHS.PRODACTIVE_NOT_ENABLED,
  ROUTER_PATHS.ERROR,
  ROUTER_PATHS.SERVER_ERROR
]

const isRootPath = (pathname: string) =>
  rootPaths.some((path) => pathname === path || pathname.startsWith(`${path}?`))

const getReportPermission = (pathname: string) =>
  Object.entries(REPORT_ROUTE_PERMISSIONS).find(([path]) =>
    pathname.includes(path)
  )?.[1]

const hasAnyReportPermission = (permissionNames: string[]) =>
  permissionNames.includes(PERMISSIONS.INCOME_REPORT_MANAGE) ||
  permissionNames.includes(PERMISSIONS.OPERATOR_EFFICIENCY_REPORT_MANAGE)

const getLandingPage = (permissionNames: string[]) => {
  const hasOrderAccess = permissionNames.some((p) =>
    p.startsWith(ORDER_PERMISSION_PREFIX)
  )

  if (hasOrderAccess) return ROUTER_PATHS.ORDERS
  if (permissionNames.includes(PERMISSIONS.EARNING_RATE_MANAGE))
    return ROUTER_PATHS.ADMINISTRATION_RATES
  if (hasAnyReportPermission(permissionNames)) return ROUTER_PATHS.REPORTS

  return ROUTER_PATHS.FORBIDDEN
}

const getAdminModule = (pathname: string) => {
  const match = pathname.match(
    new RegExp(`${ROUTER_PATHS.ADMINISTRATION}/([^/?]+)`)
  )
  return match?.[1]
}

const hasAdminModuleAccess = (permissionNames: string[], module: string) => {
  const modulePermissions = ADMIN_MODULE_PERMISSIONS[module]
  if (!modulePermissions) return false

  return modulePermissions.access.some((p) => permissionNames.includes(p))
}

const RedirectHandler = ({ children }) => {
  const router = useRouter()
  const { account } = useUser()

  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (account.prodactiveEnabled !== true) {
      router.replaceRoot(ROUTER_PATHS.PRODACTIVE_NOT_ENABLED)
      return
    }

    const replaceWithReady = (path: string) => {
      router.replaceRoot(path).then(() => setReady(true))
    }

    const pathname = router.asPath
    const orgId = getOrganizationId()
    const permissionNames = account.permissionsNames
    const landingPage = getLandingPage(permissionNames)

    if (isRootPath(pathname)) {
      //eslint-disable-next-line
      setReady(true)
      return
    }

    if (landingPage === ROUTER_PATHS.FORBIDDEN) {
      replaceWithReady(landingPage)
      return
    }

    if (pathname === '/') {
      replaceWithReady(`/${orgId}${landingPage}`)
      return
    }

    const isReportsArea =
      pathname.includes(ROUTER_PATHS.REPORTS) || !!getReportPermission(pathname)

    if (
      landingPage !== ROUTER_PATHS.ORDERS &&
      !pathname.includes(ROUTER_PATHS.ADMINISTRATION) &&
      !isReportsArea
    ) {
      replaceWithReady(`/${orgId}${landingPage}`)
      return
    }

    const adminModule = getAdminModule(pathname)
    if (adminModule && !hasAdminModuleAccess(permissionNames, adminModule)) {
      replaceWithReady(`/${orgId}${landingPage}`)
      return
    }

    const reportPermission = getReportPermission(pathname)
    if (reportPermission && !permissionNames.includes(reportPermission)) {
      replaceWithReady(`/${orgId}${landingPage}`)
      return
    }

    if (
      pathname.includes(ROUTER_PATHS.REPORTS) &&
      !hasAnyReportPermission(permissionNames)
    ) {
      replaceWithReady(ROUTER_PATHS.FORBIDDEN)
      return
    }

    const url = handleOrganizationRoutes({
      pathname,
      orgId,
      switchOrganizationPath: ROUTER_PATHS.SWITCH_ORGANIZATION
    })

    if (url) {
      replaceWithReady(url)
    }
    setReady(true)
  }, [router, account])

  if (!ready) return null

  return children
}

export default RedirectHandler
